
import { Canvas } from "fabric";
import { Tool } from "@/hooks/use-canvas-setup";
import { CanvasContainer } from "@/components/canvas/CanvasContainer";
import { ZoomControls } from "@/components/ZoomControls";
import { MainToolbar } from "@/components/toolbar/MainToolbar";
import { useState, useEffect } from "react";
import { ImageAnalysis } from "@/components/analysis/ImageAnalysis";
import { useLayerManagement } from "@/hooks/use-layer-management";

interface StudioLayoutProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  canvas: Canvas | null;
  activeTool: Tool;
  isCanvasEmpty: boolean;
  showPricing: boolean;
  isToolbarVisible: boolean;
  projectTitle: string;
  currentDrawingId: string | null;
  hasSelection: boolean;
  onToolToggle: (tool: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onDelete: () => void;
  onClear: () => void;
  onTitleChange: (title: string) => void;
  onDrawingSaved: (id: string) => void;
  setIsToolbarVisible: (visible: boolean) => void;
  onRefreshDrawings: () => void;
  rawMode?: boolean;
  onRawModeChange?: (enabled: boolean) => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onResetAnalysis?: () => void;
  layerManagement?: ReturnType<typeof useLayerManagement>;
}

// Helper for localStorage
const getStoredAnalysisPanelState = (): boolean => {
  try {
    const stored = localStorage.getItem('analysis_panel_open');
    return stored ? stored === 'true' : false;
  } catch (e) {
    return false;
  }
};

export const StudioLayout = ({
  canvasRef,
  canvas,
  activeTool,
  isCanvasEmpty,
  showPricing,
  isToolbarVisible,
  projectTitle,
  currentDrawingId,
  hasSelection,
  onToolToggle,
  onUndo,
  onRedo,
  onDelete,
  onClear,
  onTitleChange,
  onDrawingSaved,
  setIsToolbarVisible,
  onRefreshDrawings,
  rawMode,
  onRawModeChange,
  canUndo,
  canRedo,
  onResetAnalysis,
  layerManagement,
}: StudioLayoutProps) => {
  const [currentZoom, setCurrentZoom] = useState(100);
  const [isAnalysisPanelOpen, setIsAnalysisPanelOpen] = useState(getStoredAnalysisPanelState());
  
  const handleZoomChange = (zoom: number) => {
    setCurrentZoom(zoom);
  };

  // Store analysis panel state in localStorage
  useEffect(() => {
    try {
      localStorage.setItem('analysis_panel_open', isAnalysisPanelOpen.toString());
    } catch (e) {
      console.error('Failed to store analysis panel state:', e);
    }
  }, [isAnalysisPanelOpen]);
  
  return (
    <div className="flex-1 relative overflow-visible">
      <div className="absolute inset-0 overflow-visible">
        <CanvasContainer
          canvasRef={canvasRef}
          activeTool={activeTool}
          showPricing={showPricing}
          currentZoom={currentZoom}
        />
        
        <div className="absolute left-4 bottom-4 z-50">
          <ZoomControls canvas={canvas} onZoomChange={handleZoomChange} />
        </div>

        <MainToolbar
          canvas={canvas}
          activeTool={activeTool}
          onToolToggle={onToolToggle}
          hasSelection={hasSelection}
          onUndo={onUndo}
          onRedo={onRedo}
          onDelete={onDelete}
          onClear={onClear}
          projectTitle={projectTitle}
          onTitleChange={onTitleChange}
          currentDrawingId={currentDrawingId}
          onDrawingSaved={onDrawingSaved}
          isToolbarVisible={isToolbarVisible}
          setIsToolbarVisible={setIsToolbarVisible}
          onRefreshDrawings={onRefreshDrawings}
          rawMode={rawMode}
          onRawModeChange={onRawModeChange}
          showPricing={showPricing}
          canUndo={canUndo}
          canRedo={canRedo}
          onResetAnalysis={onResetAnalysis}
          isAnalysisPanelOpen={isAnalysisPanelOpen}
          setIsAnalysisPanelOpen={setIsAnalysisPanelOpen}
          layerManagement={layerManagement}
        />
        
        <ImageAnalysis 
          canvas={canvas}
          activeTool={activeTool}
          currentDrawingId={currentDrawingId}
          onResetAnalysis={onResetAnalysis}
          isOpen={isAnalysisPanelOpen}
          setIsOpen={setIsAnalysisPanelOpen}
          onToolChange={onToolToggle}
        />
      </div>
    </div>
  );
};
